import axios, {AxiosRequestConfig} from 'axios';

import {envConfig} from 'config';
import {ConversationSummaryData} from 'interfaces';
import {SessionHistory} from 'interfaces/session-history';
import {PaginatedData} from 'types';
import {apiUtils} from 'utils';

import {API_URL} from './constants';

const dataUrl = `${API_URL}/data`;
const sessionHistoryUrl = `${dataUrl}/session-history`;
const sessionHistoryFilesUrl = `${envConfig.fileServerApiUrl}/file-server/session-history`;

const getSessionHistory = async (filters, {page, pageSize}): Promise<PaginatedData<SessionHistory>> => {
    const response = await axios.post(sessionHistoryUrl, {filters, pagination: {page, pageSize}});
    return response.data;
};

const getSessionHistoryReport = async (filters, {page}, labels): Promise<PaginatedData<string[]>> => {
    const response = await axios.post(`${sessionHistoryUrl}/report`, {labels, pagination: {page}, filters});
    return response.data;
};

const getSessionHistoryConversationSummary = async (sessionId: string, activityId: string): Promise<ConversationSummaryData> => {
    const response = await axios.post(`${sessionHistoryUrl}/${sessionId}/activity/${activityId}/conversation-summary`);
    return response.data;
};

const getSessionHistoryChat = async (sessionId: string): Promise<SessionHistory> => {
    const response = await axios.post(`${sessionHistoryUrl}/${sessionId}/text-chat`);
    return response.data;
};

const getAvatarHistoryChat = async (sessionId: string): Promise<SessionHistory> => {
    const response = await axios.post(`${sessionHistoryUrl}/${sessionId}/avatar`);
    return response.data;
};

const getAgentChatbotHistoryChat = async (sessionId: string): Promise<SessionHistory> => {
    const response = await axios.post(`${sessionHistoryUrl}/${sessionId}/agent-chatbot`);
    return response.data;
};

const getSessionHistoryAudioTranscription = async (sessionId: string, activityId: string): Promise<SessionHistory> => {
    const response = await axios.post(`${sessionHistoryUrl}/${sessionId}/transcription/${activityId}`);
    return response.data;
};

const getSessionHistorySIPChat = async (sessionId: string): Promise<SessionHistory> => {
    const response = await axios.post(`${sessionHistoryUrl}/${sessionId}/sip`);
    return response.data;
};

const downloadAfterSessionFile = async (sessionId: string, fileName: string) => {
    const response = await axios.post(`${sessionHistoryFilesUrl}/after-session-file`, {sessionId, fileName});
    return apiUtils.saveFileFromBase64(fileName, response.data);
};

const downloadExternalAfterSessionFile = (sessionId: string, fileName: string, config?: AxiosRequestConfig) =>
    apiUtils.downloadFile({
        url: `${sessionHistoryUrl}/external-after-session-file`,
        method: 'POST',
        data: {sessionId, fileName},
        ...config
    }, fileName);

const getExternalAfterSessionFilePreviewUrl = async (sessionId: string, fileName: string): Promise<string> => {
    const response = await axios.post<{url: string}>(
        `${sessionHistoryUrl}/external-after-session-file/preview-url`,
        {sessionId, fileName}
    );
    return response.data.url;
};

const downloadAfterSessionFiles = (filters, fileName: string, config?: AxiosRequestConfig) =>
    apiUtils.downloadFile({url: `${sessionHistoryFilesUrl}/after-session-files`, method: 'POST', data: {filters}, ...config}, fileName);

const getAfterSessionFilesSize = async (socketId, filters): Promise<{taskId: string}> => {
    const response = await axios.post(`${sessionHistoryFilesUrl}/after-session-files-size`, {socketId, filters});
    return response.data;
};

const getActivityStatistics = async (socketId, filters): Promise<{taskId: string}> => {
    const response = await axios.post(`${sessionHistoryUrl}/activity-statistics`, {socketId, filters});
    return response.data;
};

const getShortActivityStatistics = async (socketId, filters): Promise<{taskId: string}> => {
    const response = await axios.post(`${sessionHistoryUrl}/short-activity-statistics`, {socketId, filters});
    return response.data;
};

const getAgentsStatistic = async (socketId, filters): Promise<{taskId: string}> => {
    const response = await axios.post(`${sessionHistoryUrl}/agents-statistic`, {socketId, filters});
    return response.data;
};

const getSumaReport = async (sessionId: string, participantId: string, verificationId: string): Promise<any> => {
    const response = await axios.get(`${sessionHistoryUrl}/${sessionId}/suma-report/${participantId}/${verificationId}`, {
        responseType: 'arraybuffer'
    });
    return response.data;
};

export const sessionHistoryApi = {
    getSessionHistory,
    downloadAfterSessionFile,
    downloadExternalAfterSessionFile,
    getExternalAfterSessionFilePreviewUrl,
    downloadAfterSessionFiles,
    getAfterSessionFilesSize,
    getSessionHistoryReport,
    getActivityStatistics,
    getShortActivityStatistics,
    getSessionHistoryAudioTranscription,
    getSessionHistoryChat,
    getSessionHistorySIPChat,
    getAvatarHistoryChat,
    getAgentChatbotHistoryChat,
    getSessionHistoryConversationSummary,
    getAgentsStatistic,
    getSumaReport
};
